import { ERROR_STATUS, ErrorEnvelopeSchema, type ErrorCode } from '../common/errors.js';

/**
 * The typed end of the canonical error envelope. Every failure the client can
 * produce — HTTP error, transport failure, abort, contract violation — arrives
 * as one of these, so callers never branch on `instanceof TypeError`.
 */
export class ApiError extends Error {
  readonly code: ErrorCode;
  /** HTTP status, or 0 for a client-side failure that never reached the server. */
  readonly status: number;
  readonly traceId: string;
  readonly details: unknown;

  constructor(init: {
    readonly code: ErrorCode;
    readonly message: string;
    readonly status?: number;
    readonly traceId?: string;
    readonly details?: unknown;
    readonly cause?: unknown;
  }) {
    super(init.message, init.cause === undefined ? undefined : { cause: init.cause });
    this.name = 'ApiError';
    this.code = init.code;
    this.status = init.status ?? ERROR_STATUS[init.code];
    this.traceId = init.traceId ?? 'unknown';
    this.details = init.details;
  }

  get isRevisionConflict(): boolean {
    return this.code === 'REVISION_CONFLICT';
  }

  get isAuthFailure(): boolean {
    return this.code === 'UNAUTHENTICATED' || this.code === 'FORBIDDEN';
  }

  static is(value: unknown): value is ApiError {
    return value instanceof ApiError;
  }
}

/** Status → code, for responses whose body is not a valid envelope (proxy 502s, etc). */
const STATUS_FALLBACK: ReadonlyMap<number, ErrorCode> = new Map([
  [400, 'VALIDATION_FAILED'],
  [401, 'UNAUTHENTICATED'],
  [403, 'FORBIDDEN'],
  [404, 'NOT_FOUND'],
  [409, 'CONFLICT'],
  [413, 'PAYLOAD_TOO_LARGE'],
  [415, 'UNSUPPORTED_MEDIA_TYPE'],
  [422, 'ONTOLOGY_INVALID'],
  [429, 'RATE_LIMITED'],
  [503, 'DEPENDENCY_UNAVAILABLE'],
] satisfies readonly [number, ErrorCode][]);

export const errorCodeForStatus = (status: number): ErrorCode =>
  STATUS_FALLBACK.get(status) ?? 'INTERNAL';

/**
 * Parse a failure body into an `ApiError`. A body that is not the canonical
 * envelope is not trusted into the type system — it degrades to a status-derived
 * code with the raw text as details.
 */
export const apiErrorFromBody = (status: number, rawBody: string): ApiError => {
  let parsedJson: unknown;
  try {
    parsedJson = JSON.parse(rawBody) as unknown;
  } catch {
    return new ApiError({
      code: errorCodeForStatus(status),
      message: rawBody.length > 0 ? rawBody.slice(0, 500) : `Request failed with status ${status}`,
      status,
    });
  }

  const envelope = ErrorEnvelopeSchema.safeParse(parsedJson);
  if (!envelope.success) {
    return new ApiError({
      code: errorCodeForStatus(status),
      message: `Request failed with status ${status}`,
      status,
      details: parsedJson,
    });
  }

  return new ApiError({
    code: envelope.data.error.code,
    message: envelope.data.error.message,
    status,
    traceId: envelope.data.error.traceId,
    details: envelope.data.error.details,
  });
};
